import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  Exercise,
  columns,
  equipmentFilter,
  experienceFilter,
  muscleGroups,
  typeFilter,
} from '@/components/exercises/exerciseColumns'
import { RxPlus } from 'react-icons/rx'
import { useMemo, useState } from 'react'
import { canDeleteExercise } from '@/components/exercises/exerciseAccess'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import {
  useHasPermission,
  useOrg,
  useUserId,
} from '@/lib/states/session/hooks'
import ExerciseDialog from '@/components/exercises/exerciseDialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import LoadingSpinner from '@/components/LoadingSpinner'
import { errorToast } from '@/lib/utils/ToastUtils'
import { trpc } from '@/lib/trpc/client'
import { patchListCache, removeFromList } from '@trainrr/api-client'

export const Route = createFileRoute('/_auth/_dashboard/exercises')({
  component: ExerciseList,
  pendingComponent: () => <LoadingSpinner />,
})

function ExerciseList() {
  const [open, setOpen] = useState(false)
  const [exercise, setExercise] = useState<Exercise | undefined>(undefined)
  const [exerciseToDelete, setExerciseToDelete] = useState<Exercise>()
  const org = useOrg()
  const userId = useUserId()
  const canManageLibrary = useHasPermission('exercises.manage_library')
  const tableColumns = useMemo(
    () => columns({ userId, canManageLibrary }),
    [userId, canManageLibrary],
  )

  const { data: exercises, isLoading } = trpc.exercises.list.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  )
  const utils = trpc.useUtils()
  const deleteExercise = trpc.exercises.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.exercises.list, (old) =>
        removeFromList(old, variables.id),
      )
      setExerciseToDelete(undefined)
    },
    onError: (mutationError) => {
      errorToast(mutationError, 'Error deleting exercise')
    },
  })

  const handleExerciseDeleted = (selected: Exercise) => {
    if (!canDeleteExercise(selected, { userId, canManageLibrary })) {
      return
    }
    setExerciseToDelete(selected)
  }

  const handleOnClickEdit = (selected: Exercise) => {
    if (open) {
      return
    }

    setExercise(selected)
    setOpen(true)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setExercise(undefined)
    setOpen(nextOpen)
  }

  return (
    <>
      <AlertDialog
        open={exerciseToDelete !== undefined}
        onOpenChange={() => setExerciseToDelete(undefined)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {exerciseToDelete?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will delete the exercise from
              your library.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant={'destructive'}
              onClick={() => {
                if (!exerciseToDelete?.id || !org) {
                  return
                }
                deleteExercise.mutate({ id: exerciseToDelete.id })
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Exercises</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={(exercises as Exercise[]) ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={handleExerciseDeleted}
              toggleFilter={{
                label: 'Hide Trainrr Library',
                column: 'org_id',
                filterValue: true,
              }}
              showSearchBar
              filterOptions={[
                {
                  column: 'primary_muscles',
                  title: 'Muscle groups',
                  options: muscleGroups,
                },
                {
                  column: 'equipment',
                  title: 'Equipment',
                  options: equipmentFilter,
                },
                {
                  column: 'type',
                  title: 'Type',
                  options: typeFilter,
                },
                {
                  column: 'experience',
                  title: 'Experience',
                  options: experienceFilter,
                },
              ]}
              isLoading={isLoading}
              stickyHeader
              tableContainerClassName="min-h-0 flex-1"
              tableClassName="table-fixed min-w-[1012px]"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
            />
          </div>
        </div>
      </div>
      <ExerciseDialog
        open={open}
        onOpenChange={handleOpenChange}
        exercise={exercise}
      />
    </>
  )
}
